import { useCallback, useRef } from 'react';

import {
  SmsPreviewFieldsFragment,
  StudentSmsStateDocument,
  StudentSmsStateFieldsFragment,
  StudentSmsStateQuery,
  StudentSmsStateQueryVariables,
  useQueueStudentSmsMutation,
  useStudentSmsPreviewQuery,
  useStudentSmsStateQuery,
} from 'core/graphql';

import { usePageVisiblePoll } from '../Meta/usePageVisiblePoll';
import { ISmsError, smsCrashErrors, toSmsErrors } from './smsFeedback';
import {
  ISmsLinkDraft,
  isSmsInFlight,
  SMS_STATE_POLL_MS,
  smsLinkInput,
} from './smsModel';
import { useSmsPermissions } from './useSmsPermissions';
import { useSmsPreviewInput } from './useSmsPreviewInput';

/**
 * ТЗ 0081 — SMS студенту из карточки и коммуникатора: можно ли, куда уйдёт,
 * какие фактуры дать ссылкой, история SMS студента (всех тем), живой
 * предпросмотр и отправка.
 */

export type IStudentSmsState = StudentSmsStateFieldsFragment;
export type ISmsPreview = SmsPreviewFieldsFragment;
export type ISmsInvoiceOption = StudentSmsStateFieldsFragment['invoices'][number];

export const useStudentSmsState = (studentId: string, enabled = true) => {
  // Право на ОДНУ SMS студенту, а не на раздел: HR читает журнал и рассылает
  // по группе, но студенту лично не пишет (решение владельца 2026-09-22).
  const { canSendStudent: canUse } = useSmsPermissions();
  const skip = !canUse || !enabled || !studentId;
  const {
    data,
    loading,
    error,
    refetch,
    startPolling,
    stopPolling,
  } = useStudentSmsStateQuery({
    variables: { studentId },
    skip,
    fetchPolicy: 'cache-and-network',
  });
  const state = data?.studentSms ?? null;
  // Опрос — только пока что-то в пути: иначе открытая карточка спрашивала
  // бы сервер каждые 5 секунд часами.
  const inFlight = Boolean(state && isSmsInFlight(state.messages));
  usePageVisiblePoll(
    startPolling,
    stopPolling,
    SMS_STATE_POLL_MS,
    !skip && inFlight,
  );
  const reload = useCallback(() => {
    refetch().catch(() => undefined);
  }, [refetch]);
  return {
    canUse,
    state,
    loading: loading && !data,
    failed: Boolean(error) && !data,
    reload,
  };
};

export interface IUseSmsPreview {
  preview: ISmsPreview | null;
  /** Предпросмотр отстал от набранного (задержка или запрос в пути). */
  pending: boolean;
  failed: boolean;
  /** Спросить сервер ещё раз: «не удалось посчитать» — не тупик. */
  reload: () => void;
}

/**
 * Живой предпросмотр SMS студенту. `network-only`: хвост и доступность
 * зависят от того, что уже ушло, — тот же текст после отправки обязан
 * пересчитаться. Пока нет ответа на новый текст, держим прошлый.
 */
export const useStudentSmsPreview = (
  studentId: string,
  text: string,
  link: ISmsLinkDraft,
  enabled = true,
): IUseSmsPreview => {
  const { canSendStudent: canUse } = useSmsPermissions();
  const { debounced, lagging, hasText } = useSmsPreviewInput(text, link);
  const skip = !canUse || !enabled || !studentId || !debounced.text;
  const {
    data,
    previousData,
    loading,
    error,
    refetch,
  } = useStudentSmsPreviewQuery({
    variables: { studentId, text: debounced.text, link: debounced.link },
    skip,
    fetchPolicy: 'network-only',
  });
  const reload = useCallback(() => {
    refetch().catch(() => undefined);
  }, [refetch]);
  if (!hasText) {
    return { preview: null, pending: false, failed: false, reload };
  }
  const current = (data ?? previousData)?.previewStudentSms ?? null;
  return {
    preview: current,
    pending: lagging || loading,
    failed: Boolean(error) && !current,
    reload,
  };
};

export interface IStudentSmsSendInput {
  text: string;
  link: ISmsLinkDraft;
  templateId: string | null;
  requestKey: string;
}

export interface IStudentSmsSendResult {
  ok: boolean;
  errors: ISmsError[];
  /** Статус поставленной SMS (`pending` / `skipped` / …). */
  status: string | null;
  /**
   * Текст поставленной SMS — по нему форма узнаёт, что повтор с тем же
   * ключом вернул ПРЕЖНЮЮ SMS (перепроверка 0084).
   */
  body?: string | null;
}

/**
 * Отправить SMS студенту. Ответ несёт полное состояние — оно пишется в
 * запрос карточки, и новая SMS встаёт в историю сразу. Не `refetchQueries`
 * по имени: так бьётся и по пропущенному запросу (грабли
 * `refetch-by-name-skipped-query`).
 */
export const useStudentSmsSend = (studentId: string) => {
  const [sendMut, { loading: sending }] = useQueueStudentSmsMutation();
  const inFlight = useRef(false);

  const send = useCallback(
    async (input: IStudentSmsSendInput): Promise<IStudentSmsSendResult> => {
      if (inFlight.current) return { ok: false, errors: [], status: null };
      inFlight.current = true;
      try {
        const { data } = await sendMut({
          variables: {
            studentId,
            text: input.text.trim(),
            link: smsLinkInput(input.link),
            templateId: input.templateId || null,
            requestKey: input.requestKey,
          },
          update: (cache, { data: answer }) => {
            const state = answer?.sendStudentSms?.state;
            if (!state) return;
            cache.writeQuery<
              StudentSmsStateQuery,
              StudentSmsStateQueryVariables
            >({
              query: StudentSmsStateDocument,
              variables: { studentId },
              data: { studentSms: state },
            });
          },
        });
        const payload = data?.sendStudentSms;
        const errors = toSmsErrors(payload?.customErrors);
        return {
          ok: Boolean(payload?.success) && !errors.length,
          errors,
          status: payload?.message?.status ?? null,
          body: payload?.message?.body ?? null,
        };
      } catch (caught) {
        return { ok: false, errors: smsCrashErrors(caught), status: null };
      } finally {
        inFlight.current = false;
      }
    },
    [sendMut, studentId],
  );

  return { send, sending };
};
